import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiSearch, FiRefreshCw } from 'react-icons/fi';
import WarningIcon from '../common/icons/WarningIcon';
import { db } from '../../services/supabase';

const tableColumns = [
  { key: 'first_name', label: 'Meno' },
  { key: 'last_name', label: 'Priezvisko' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Telefón' },
  { key: 'city', label: 'Mesto' },
  { key: 'ticket_count', label: 'Lístky' },
  { key: 'total_amount', label: 'Suma' },
  { key: 'status', label: 'Status' },
  { key: 'created_at', label: 'Vytvorené' },
];

const statusBadgeVariants = {
  paid: 'bg-success-500/15 text-success-200 border-success-500/40',
  pending: 'bg-warning-500/15 text-warning-200 border-warning-500/40',
  cancelled: 'bg-error-500/15 text-error-200 border-error-500/40',
  refunded: 'bg-secondary-500/15 text-secondary-200 border-secondary-500/40',
};

const CustomerTable = () => {
  const [search, setSearch] = useState('');

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat('sk-SK', { maximumFractionDigits: 0 }),
    []
  );

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('sk-SK', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    []
  );

  /**
   * Načítame všetky objednávky – slúžia ako zdroj dat pre tabuľku zákazníkov.
   * V produkcii by sme ideálne mali mať vlastnú tabuľku customers,
   * no v súčasnej schéme poznáme zákazníkov práve cez objednávky.
   */
  const {
    data: orders,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['customer-orders'],
    queryFn: db.orders.getAll,
    staleTime: 30 * 1000,
  });

  const preparedOrders = useMemo(() => {
    if (!Array.isArray(orders)) {
      return [];
    }

    return orders.map((order) => ({
      ...order,
      tickets: Array.isArray(order.tickets) ? order.tickets : [],
      ticket_objects: Array.isArray(order.ticket_objects) ? order.ticket_objects : [],
      ticket_count: Number(order.ticket_count ?? order.tickets?.length ?? 0),
      total_amount: Number(order.total_amount ?? 0),
    }));
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (!search) return preparedOrders;
    const term = search.toLowerCase();
    return preparedOrders.filter((order) =>
      [
        order.first_name,
        order.last_name,
        order.email,
        order.phone,
        order.city,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term))
    );
  }, [preparedOrders, search]);

  const aggregates = useMemo(() => {
    const paid = preparedOrders.filter((order) => order.status === 'paid');
    const pending = preparedOrders.filter((order) => order.status === 'pending');
    const totalRevenue = paid.reduce((sum, order) => sum + order.total_amount, 0);
    const totalTickets = preparedOrders.reduce((sum, order) => sum + order.ticket_count, 0);
    const uniqueCustomers = new Set(
      preparedOrders.map((order) => `${order.email}|${order.first_name}|${order.last_name}`)
    );

    return {
      totalOrders: preparedOrders.length,
      paidOrders: paid.length,
      pendingOrders: pending.length,
      totalRevenue,
      totalTickets,
      customerCount: uniqueCustomers.size,
    };
  }, [preparedOrders]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center text-slate-200">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-400 mx-auto mb-4" />
          <p className="text-lg text-slate-300">Načítavam zákazníkov...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-12">
        <div className="text-error-400 text-6xl mb-4 flex justify-center">
          <WarningIcon className="text-5xl" aria-hidden="true" />
        </div>
        <h3 className="text-xl font-semibold text-primary-100 mb-4">Chyba pri načítaní zákazníkov</h3>
        <button onClick={() => refetch()} className="btn btn-primary">
          <FiRefreshCw className="w-4 h-4 mr-2" />
          Skúsiť znovu
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Štatistiky */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Zákazníci',
            value: numberFormatter.format(aggregates.customerCount),
            description: 'Podľa e-mailovej adresy',
          },
          {
            label: 'Objednávky',
            value: numberFormatter.format(aggregates.totalOrders),
            description: 'Celkový počet objednávok',
          },
          {
            label: 'Predané lístky',
            value: numberFormatter.format(aggregates.totalTickets),
            description: 'Súčet cez všetky objednávky',
          },
          {
            label: 'Celková suma',
            value: currencyFormatter.format(aggregates.totalRevenue),
            description: 'Sčítané zaplatené objednávky',
          },
        ].map((stat) => (
          <div
            key={stat.label}
            className="card backdrop-blur-sm border border-slate-800/60 bg-slate-900/60"
          >
            <p className="text-sm text-slate-400">{stat.label}</p>
            <p className="text-2xl font-semibold text-slate-100 mt-2">{stat.value}</p>
            <p className="text-xs text-slate-500 mt-1">{stat.description}</p>
          </div>
        ))}
      </div>

      {/* Filter + vyhľadávanie */}
      <div className="card backdrop-blur-sm border border-slate-800/60 bg-slate-900/60">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:max-w-md">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Hľadať meno, email, mesto..."
              className="w-full bg-slate-950/80 border border-slate-800 rounded-xl pl-10 pr-4 py-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
            />
          </div>
          <div className="text-sm text-slate-400">
            {filteredOrders.length === preparedOrders.length
              ? `Zobrazených ${numberFormatter.format(filteredOrders.length)} objednávok`
              : `Filtrovaných ${numberFormatter.format(filteredOrders.length)} z ${numberFormatter.format(
                  preparedOrders.length
                )} objednávok`}
          </div>
        </div>
      </div>

      {/* Tabuľka zákazníkov - Desktop */}
      <div className="hidden md:block card backdrop-blur-sm border border-slate-800/60 bg-slate-900/60 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-800">
          <thead>
            <tr className="text-left text-xs font-semibold uppercase text-slate-400 tracking-wider">
              {tableColumns.map((column) => (
                <th key={column.key} className="px-4 py-3">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {filteredOrders.length === 0 ? (
              <tr>
                <td
                  colSpan={tableColumns.length}
                  className="px-4 py-12 text-center text-slate-500"
                >
                  Žiadni zákazníci nezodpovedajú vyhľadávaniu.
                </td>
              </tr>
            ) : (
              filteredOrders.map((order) => {
                const badgeVariant =
                  statusBadgeVariants[order.status] ?? 'bg-slate-800 text-slate-200 border-slate-700';

                return (
                  <tr key={order.id} className="hover:bg-slate-800/40 transition-colors">
                    <td className="px-4 py-3 text-slate-100">{order.first_name}</td>
                    <td className="px-4 py-3 text-slate-100">{order.last_name}</td>
                    <td className="px-4 py-3 text-slate-300">{order.email}</td>
                    <td className="px-4 py-3 text-slate-300">{order.phone ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-300">{order.city ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-100">
                      <div className="flex flex-col gap-1">
                        <span className="font-medium">
                          {numberFormatter.format(order.ticket_count)}
                        </span>
                        {order.ticket_objects && order.ticket_objects.length > 0 && (
                          <div className="text-xs text-slate-400 flex flex-wrap gap-1">
                            {order.ticket_objects.map((ticket, idx) => (
                              <span
                                key={ticket.id || idx}
                                className="px-1.5 py-0.5 bg-slate-800/60 rounded border border-slate-700/50"
                                title={`Sekcia ${ticket.section}, číslo ${ticket.number_in_section}`}
                              >
                                {ticket.ticket_number || `${ticket.section}${ticket.number_in_section}`}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-100">
                      {currencyFormatter.format(order.total_amount)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium border rounded-full ${badgeVariant}`}
                      >
                        {order.status === 'paid'
                          ? 'Zaplatené'
                          : order.status === 'pending'
                          ? 'Čaká'
                          : order.status === 'cancelled'
                          ? 'Zrušené'
                          : order.status === 'refunded'
                          ? 'Refundované'
                          : order.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {order.created_at
                        ? new Date(order.created_at).toLocaleString('sk')
                        : '—'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Kartičky zákazníkov - Mobile */}
      <div className="md:hidden space-y-4">
        {filteredOrders.length === 0 ? (
          <div className="card backdrop-blur-sm border border-slate-800/60 bg-slate-900/60 text-center py-12">
            <p className="text-slate-500">Žiadni zákazníci nezodpovedajú vyhľadávaniu.</p>
          </div>
        ) : (
          filteredOrders.map((order) => {
            const badgeVariant =
              statusBadgeVariants[order.status] ?? 'bg-slate-800 text-slate-200 border-slate-700';

            return (
              <div
                key={order.id}
                className="card backdrop-blur-sm border border-slate-800/60 bg-slate-900/60"
              >
                <div className="space-y-4">
                  {/* Hlavička */}
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-100">
                        {order.first_name} {order.last_name}
                      </h3>
                      <p className="text-sm text-slate-400 mt-1">{order.email}</p>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium border rounded-full ${badgeVariant}`}
                    >
                      {order.status === 'paid'
                        ? 'Zaplatené'
                        : order.status === 'pending'
                        ? 'Čaká'
                        : order.status === 'cancelled'
                        ? 'Zrušené'
                        : order.status === 'refunded'
                        ? 'Refundované'
                        : order.status}
                    </span>
                  </div>

                  {/* Detaily */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-800/60">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Telefón</p>
                      <p className="text-sm text-slate-200">{order.phone ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Mesto</p>
                      <p className="text-sm text-slate-200">{order.city ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Lístky</p>
                      <p className="text-sm font-semibold text-slate-100">
                        {numberFormatter.format(order.ticket_count)}
                      </p>
                      {order.ticket_objects && order.ticket_objects.length > 0 && (
                        <div className="text-xs text-slate-400 flex flex-wrap gap-1 mt-1">
                          {order.ticket_objects.slice(0, 5).map((ticket, idx) => (
                            <span
                              key={ticket.id || idx}
                              className="px-1.5 py-0.5 bg-slate-800/60 rounded border border-slate-700/50"
                            >
                              {ticket.ticket_number || `${ticket.section}${ticket.number_in_section}`}
                            </span>
                          ))}
                          {order.ticket_objects.length > 5 && (
                            <span className="text-slate-500">+{order.ticket_objects.length - 5}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Suma</p>
                      <p className="text-lg font-bold text-primary-200">
                        {currencyFormatter.format(order.total_amount)}
                      </p>
                    </div>
                  </div>

                  {/* Dátum */}
                  <div className="pt-2 border-t border-slate-800/60">
                    <p className="text-xs text-slate-500">
                      Vytvorené: {order.created_at ? new Date(order.created_at).toLocaleString('sk') : '—'}
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CustomerTable;